// App.tsx
import {lazy, Suspense, useEffect, useState} from 'react';
import {BrowserRouter} from 'react-router-dom';

import ConsoleLogger from "@/components/console-logger.tsx";
import {ChunkLoadErrorBoundary} from "@/components/error-boundaries/ChunkLoadErrorBoundary.tsx";
import {OfflineIndicator} from "@/components/offline-indicator.tsx";
import {ToastProvider} from "@/components/toast-provider.tsx";
import EnhancedSplashScreen from "@/features/Home/enhanced-splash-screen.tsx";
import {cn} from "@/lib/utils.ts";
import {DebugProvider, useDebug} from "@/providers/debug-context.tsx";
import {DeviceProvider} from "@/providers/device-context.tsx";
import {ThemeProvider} from "@/providers/theme-provider.tsx";

// Lazy load login and role-specific content
const AppRoutes = lazy(() => import('../app-routes.tsx'));
const KVMetricsDisplay = lazy(() => import('@/components/kv-metric-display.tsx'));

const DebugComponents = () => {
    const {isDebugEnabled} = useDebug();

    if (!isDebugEnabled) return null;

    return (
        <Suspense fallback={null}>
            <div
                className="flex flex-col justify-between fixed bottom-4 z-50 opacity-90 hover:opacity-100 transition-opacity">
                <KVMetricsDisplay/>
                <ConsoleLogger/>
            </div>
        </Suspense>
    );
};

function App() {
    const [showSplash, setShowSplash] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [animationComplete, setAnimationComplete] = useState(false);
    const [contentVisible, setContentVisible] = useState(false);
    const isDevelopment = true// import.meta.env.MODE === 'development';


    useEffect(() => {
        // Preload the main routes (includes login and admin)
        import('../app-routes.tsx')
            .then(() => {
                if (document.readyState === 'complete') {
                    setIsLoading(false);
                } else {
                    window.addEventListener('load', () => setIsLoading(false));
                }
            })
            .catch(error => {
                console.error('Failed to load main routes:', error);
                setIsLoading(false);
            });

        return () => {
            window.removeEventListener('load', () => setIsLoading(false));
        };
    }, []);

    const handleAnimationComplete = () => {
        setAnimationComplete(true);
    };

    const handleSplashComplete = () => {
        if (animationComplete && !isLoading) {
            setContentVisible(true);
            setTimeout(() => {
                setShowSplash(false);
            }, 400);
        }
    };

    return (
        <BrowserRouter>
            <DeviceProvider>
                <ThemeProvider>
                    <DebugProvider>
                        <ToastProvider>

                            {/* Main container - simplified */}
                            <div className="flex flex-col h-full min-h-0">
                                {showSplash && (
                                    <div className="absolute inset-0 z-50">
                                        <EnhancedSplashScreen
                                            isLoading={isLoading}
                                            onAnimationComplete={handleAnimationComplete}
                                            onLoadingComplete={handleSplashComplete}
                                        />
                                    </div>
                                )}

                                {/* Main content area */}
                                <Suspense fallback={null}>
                                    <ChunkLoadErrorBoundary>
                                        <div className={cn(
                                            "flex-1 min-h-0", // Important for nested flex scrolling
                                            contentVisible ? 'opacity-100' : 'opacity-0',
                                            'transition-opacity duration-1000 ease-in-out'
                                        )}>
                                            <AppRoutes/>
                                        </div>
                                    </ChunkLoadErrorBoundary>
                                </Suspense>

                                <div className="flex flex-col justify-between">
                                    <div><OfflineIndicator/></div>
                                </div>
                                {isDevelopment &&
                                    <DebugComponents/>}
                            </div>

                        </ToastProvider>
                    </DebugProvider>
                </ThemeProvider>
            </DeviceProvider>
        </BrowserRouter>
    );
}

export default App;